import { CreateElement, VNodeData } from 'vue';
import { Component, Prop, Vue } from 'vue-property-decorator';

type ClSvgIconColor =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'action'
  | 'error'
  | 'disabled'
  | 'warning'
  | 'success';
'nativeColor';

@Component
export default class ClSvgIcon extends Vue {
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * You can use the `nativeColor` property to apply a color attribute to the SVG element.
   */
  @Prop({
    type: String,
    default: 'inherit',
    validator: x =>
      [
        'inherit',
        'primary',
        'secondary',
        'action',
        'error',
        'disabled',
        'warning',
        'success',
        'nativeColor'
      ].includes(x)
  })
  public color!: ClSvgIconColor;

  /**
   * The component used for the root node.
   */
  @Prop({
    type: String,
    default: 'svg'
  })
  public component!: string;

  /**
   * The fontSize applied to the icon. Defaults to 24px, but can be configured to inherit font size.
   */
  @Prop({
    type: String,
    validator: x => ['inherit', 'default', 'small', 'large'].includes(x),
    default: 'default'
  })
  public fontSize!: string;

  /**
   * Applies a color attribute to the SVG element.
   */
  @Prop(String)
  public nativeColor!: string;

  /**
   * Allows you to redefine what the coordinates without units mean inside an SVG element.
   * For example, if the SVG element is 500 (width) by 200 (height),
   * and you pass viewBox="0 0 50 20",
   * this means that the coordinates inside the SVG will go from the top left corner (0,0)
   * to bottom right (50,20) and each unit will be worth 10px.
   */
  @Prop({
    type: String,
    default: '0 0 24 24'
  })
  public viewBox!: string;

  public render(h: CreateElement) {
    const data: VNodeData = {
      attrs: {
        color: this.nativeColor,
        focusable: false,
        role: 'presentation',
        viewBox: this.viewBox
      },
      class: {
        'cl-svg-icon': true,
        [`cl-svg-icon--color-${this.color}`]: Boolean(this.color),
        [`cl-svg-icon--font-size-${this.fontSize}`]: true
      }
    };
    return h(this.component, data, this.$slots.default);
  }
}
